import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const PaymentIntegrationPanel = () => {
  const [activeSubTab, setActiveSubTab] = useState('gateways');

  const subTabs = [
    { id: 'gateways', label: 'Payment Gateways', icon: 'CreditCard' },
    { id: 'transactions', label: 'Transactions', icon: 'ArrowRightLeft' },
    { id: 'settings', label: 'Settings', icon: 'Settings' }
  ];

  const mockGateways = [
    { id: 1, name: 'Razorpay', status: 'active', fee: '2.5%', icon: 'CreditCard' },
    { id: 2, name: 'PayPal', status: 'inactive', fee: '3.0%', icon: 'CreditCard' },
    { id: 3, name: 'Stripe', status: 'active', fee: '2.9%', icon: 'CreditCard' },
    { id: 4, name: 'Paytm', status: 'inactive', fee: '2.0%', icon: 'CreditCard' }
  ];

  const mockTransactions = [
    { id: 1, vendor: 'Rajesh Photography', amount: '₹2,499', status: 'completed', date: '2024-01-15', gateway: 'Razorpay' },
    { id: 2, vendor: 'Elegant Venues', amount: '₹4,999', status: 'pending', date: '2024-01-14', gateway: 'Stripe' },
    { id: 3, vendor: 'Mehndi by Priya', amount: '₹999', status: 'completed', date: '2024-01-13', gateway: 'Razorpay' }
  ];

  const renderSubTabContent = () => {
    switch (activeSubTab) {
      case 'gateways':
        return (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-soft p-6">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
                <h3 className="font-heading font-semibold text-lg text-text-primary">
                  Payment Gateways
                </h3>
                <Button variant="primary" iconName="Plus" iconSize={16}>
                  Add Gateway
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {mockGateways.map((gateway) => (
                  <div key={gateway.id} className="border border-border rounded-xl p-6 hover:border-primary-200 transition-colors">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
                          <Icon name={gateway.icon} size={20} className="text-primary" />
                        </div>
                        <h4 className="font-heading font-semibold text-lg text-text-primary">
                          {gateway.name}
                        </h4>
                      </div>
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        gateway.status === 'active' ? 'bg-success-100 text-success' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {gateway.status}
                      </span>
                    </div>
                    
                    <div className="mb-4">
                      <span className="text-text-secondary text-sm">Transaction Fee: </span>
                      <span className="font-medium text-text-primary">{gateway.fee}</span>
                    </div>
                    
                    <div className="flex space-x-2">
                      <Button variant="outline" size="sm" className="flex-1">
                        Configure
                      </Button>
                      <Button variant="ghost" size="sm">
                        <Icon name="MoreVertical" size={16} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      case 'transactions':
        return (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-soft p-6">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
                <h3 className="font-heading font-semibold text-lg text-text-primary">
                  Recent Transactions
                </h3>
                <Button variant="outline" iconName="Download" iconSize={16}>
                  Export
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <Input placeholder="Search transactions..." icon="Search" />
                <select className="px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500">
                  <option>All Status</option>
                  <option>Completed</option>
                  <option>Pending</option>
                  <option>Failed</option>
                </select>
                <select className="px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500">
                  <option>All Gateways</option>
                  <option>Razorpay</option>
                  <option>Stripe</option>
                  <option>PayPal</option>
                </select>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-surface-100">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary uppercase">
                        Vendor
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary uppercase">
                        Amount
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary uppercase">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary uppercase">
                        Gateway
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary uppercase">
                        Date
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary uppercase">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {mockTransactions.map((transaction) => (
                      <tr key={transaction.id} className="hover:bg-surface-50">
                        <td className="px-4 py-3">
                          <div className="flex items-center">
                            <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
                              <Icon name="User" size={16} className="text-primary" />
                            </div>
                            <span className="font-medium text-text-primary">{transaction.vendor}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 font-medium text-text-primary">{transaction.amount}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                            transaction.status === 'completed' ? 'bg-success-100 text-success' : 
                            transaction.status === 'pending'? 'bg-warning-100 text-warning' : 'bg-red-100 text-red-600'
                          }`}>
                            {transaction.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-text-secondary">{transaction.gateway}</td>
                        <td className="px-4 py-3 text-text-secondary">{transaction.date}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center space-x-2">
                            <button className="text-primary hover:text-primary-600">
                              <Icon name="Eye" size={16} />
                            </button>
                            <button className="text-gray-500 hover:text-gray-700">
                              <Icon name="Download" size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );
      case 'settings':
        return (
          <div className="bg-white rounded-xl shadow-soft p-6">
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Settings" size={32} className="text-primary" />
              </div>
              <h3 className="font-heading font-semibold text-xl text-text-primary mb-2">
                Payment Settings
              </h3>
              <p className="text-text-secondary mb-6">
                Configure payment processing settings and fees
              </p>
              <Button variant="outline" disabled>
                Configure Settings
              </Button>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-primary to-secondary rounded-xl p-6 text-white">
        <h2 className="font-heading font-bold text-2xl mb-2">
          Payment Integration
        </h2>
        <p className="text-white/90">
          Manage payment gateways and transactions
        </p>
      </div>

      {/* Sub Navigation */}
      <div className="bg-white rounded-xl shadow-soft p-6">
        <div className="flex flex-wrap gap-2">
          {subTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveSubTab(tab.id)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                activeSubTab === tab.id
                  ? 'bg-primary text-white' :'text-text-secondary hover:bg-surface-100'
              }`}
            >
              <Icon name={tab.icon} size={16} />
              <span className="font-medium">{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Sub Tab Content */}
      {renderSubTabContent()}
    </div>
  );
};

export default PaymentIntegrationPanel;